import React, { useRef } from "react";
import { Button, Grid } from "@material-ui/core";
import { Formik } from "formik";
import { isEmpty } from "lodash";
import styled, { css } from "styled-components";

import CriteriaService from "./CriteriaService";
import CriteriaRow from "./CriteriaRow";
import ExtjsCodeMirror from "app/common/components/input/ExtjsCodeMirror";

export default function CriteriaPanel({
  onChange,
  value,
  lookupColumns,
  lookupListId,
  currentSheetColumns,
  currentListId,
  hiddenMultiLevels,
  floatingCriteriaButton = false,
  addButtonText = "+ Add Filter",
  isCenterAlignedCriteriaButton = false,
  isColumnTypeDisabled = true,
  colorValIndex,
  forDuplication = false
}) {
  const criteriaService = React.useMemo(
    () => new CriteriaService(lookupColumns),
    [lookupColumns]
  );
  const { getEmptyRow } = criteriaService;
  const initialCriteriaFormatted = React.useMemo(
    () => criteriaService.getInitialCriteriaFormatted(value),
    [criteriaService, value]
  );
  
  const [
    isAdvancedCriteriaShownOnLoad,
    setIsAdvancedCriteriaShownOnLoad,
  ] = React.useState(
    isEmpty(initialCriteriaFormatted) &&
      !isEmpty(typeof value === "string" ? value : value.query_string)
  );

  return (
    <Formik
      initialValues={{
        rows: isEmpty(initialCriteriaFormatted)
          ? [getEmptyRow()]
          : initialCriteriaFormatted,
        criteriaString:
          typeof value === "string"
            ? value
            : criteriaService.getCriteriaForSave(
                initialCriteriaFormatted || [getEmptyRow()]
              ) || value.query_string,
      }}
    >
      {(props) => (
        <CriteriaForm
          {...props}
          lookupColumns={lookupColumns}
          lookupListId={lookupListId}
          currentSheetColumns={currentSheetColumns}
          currentListId={currentListId}
          hiddenMultiLevels={hiddenMultiLevels}
          onChange={onChange}
          isAdvancedCriteriaShownOnLoad={isAdvancedCriteriaShownOnLoad}
          criteriaService={criteriaService}
          floatingCriteriaButton={floatingCriteriaButton}
          addButtonText={addButtonText}
          isCenterAlignedCriteriaButton={isCenterAlignedCriteriaButton}
          isColumnTypeDisabled={isColumnTypeDisabled}
          cpIndex={colorValIndex || 0}
          forDuplication = {forDuplication}
        />
      )}
    </Formik>
  );
}

function CriteriaForm({
  values,
  setFieldValue,
  lookupColumns,
  lookupListId,
  currentSheetColumns,
  currentListId,
  hiddenMultiLevels,
  onChange,
  isAdvancedCriteriaShownOnLoad,
  criteriaService,
  floatingCriteriaButton,
  addButtonText,
  isCenterAlignedCriteriaButton = { isCenterAlignedCriteriaButton },
  isColumnTypeDisabled,
  cpIndex,
  forDuplication = false
}) {
  const [isFirstRender, setIsFirstRender] = React.useState(true);
  const [isAdvancedCriteriaShown, setAdvancedCriteriaShown] = React.useState(
    isAdvancedCriteriaShownOnLoad
  );
  const [isCriteriaTextEdited, setIsCriteriaTextEdited] = React.useState(false);

  const { getEmptyRow } = criteriaService;

  const handleAddRow = (condition) => {
    setFieldValue("rows", [...values.rows, getEmptyRow(condition)]);
  };

  React.useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
      return;
    }

    if (isCriteriaTextEdited) {
      return;
    }

    if(values.rows.length == 1 && Object.keys(values.rows[0]).length == 3 && values.rows[0].rhsType == 0 && !values.rows[0].condition) {
      return;
    }
    const criteriaString = criteriaService.getCriteriaForSave(values.rows);
    setFieldValue("criteriaString", criteriaString);
    onChange(criteriaString);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteriaService, onChange, setFieldValue, values.rows]);

  const handleCriteriaStringChange = React.useCallback(
    (val) => {
      setIsCriteriaTextEdited(true);
      setFieldValue("criteriaString", val);
      onChange(val);
    },
    [setFieldValue, onChange]
  );

  const CriteriaButton = floatingCriteriaButton ? FloatingButton : Button;

  const addFilterScroller = useRef(null);
  const advancedCriteriaScroller = useRef(null);

  return (
    <Grid container spacing={1}>
      <Grid container>
        {values.rows.map((row, index) => (
          <Grid item container justify="center" xs={12} key={row.key}>
            <CriteriaRow
              lookupColumns={lookupColumns}
              lookupListId={lookupListId}
              currentSheetColumns={currentSheetColumns}
              currentListId={currentListId}
              hiddenMultiLevels={hiddenMultiLevels}
              value={row}
              index={index}
              criteriaService={criteriaService}
              setFieldValue={(...args) => {
                setIsCriteriaTextEdited(false);
                setFieldValue(...args);
              }}
              isHoveredCriteriaRow={!isCenterAlignedCriteriaButton}
              isColumnTypeDisabled={isColumnTypeDisabled}
              addFilterScroller={addFilterScroller}
              forDuplication={forDuplication}
            />
          </Grid>
        ))}
      </Grid>
      {(values.rows.length != 1 || values.rows[0]?.lhs) && (
        <Grid item>
          <Button
            size="small"
            onClick={() => {
              handleAddRow("and");
              setTimeout(() => {
                addFilterScroller?.current?.scrollIntoView({
                  block: "end",
                  behaviour: "smooth",
                });
              }, 100);
            }}
            // variant="contained"
            component="a"
          >
            {addButtonText}
          </Button>
        </Grid>
      )}
      <StyledDiv
        ref={addFilterScroller}
        className={`scroller-${cpIndex}`}
      ></StyledDiv>
      {(values.criteriaString || isAdvancedCriteriaShown) && (
        <CriteriaButton
          variant="outlined"
          onClick={() => {
            setAdvancedCriteriaShown(!isAdvancedCriteriaShown);
            setTimeout(() => {
              if (!isAdvancedCriteriaShown)
              advancedCriteriaScroller?.current?.scrollIntoView({
                  block: "end",
                  behaviour: "smooth",
                });
              else
                addFilterScroller?.current?.scrollIntoView({
                  block: "end",
                  behaviour: "smooth",
                });
            }, 100);
          }}
          size="small"
          isCenterAlignedCriteriaButton={isCenterAlignedCriteriaButton}
        >
          {!isAdvancedCriteriaShown
            ? "show advanced criteria"
            : "hide advanced criteria"}
        </CriteriaButton>
      )}
      <Grid item>
        <>
          {isAdvancedCriteriaShownOnLoad && (
            <i>
              We could not represent your query in the form above. You can
              modify it by adding new criteria.
            </i>
          )}
          {isAdvancedCriteriaShown && (
            <ExtjsCodeMirror
              fullWidth
              onChange={handleCriteriaStringChange}
              mode="text/x-mysql"
              columns={[...lookupColumns, ...loggedInUserColumns]}
              emptyText=""
              name="alias_path"
              formula={values.criteriaString}
            />
          )}
          <StyledDiv
            ref={advancedCriteriaScroller}
            className={`scroller-${cpIndex}-2`}
          ></StyledDiv>
        </>
      </Grid>
    </Grid>
  );
}

const FloatingButton = styled(Button)`
  ${(props) =>
    props.isCenterAlignedCriteriaButton === false
      ? css`
          && {
            float: left;
            position: absolute;
            bottom: 10px;
            margin-left: 5px;
          }
        `
      : css`
          && {
            align-self: center;
          }
        `}
`;

const StyledDiv = styled.div`
  position: relative;
  top: 25px;
`;